import React, { useState } from "react";
import { object, string, number } from "yup";
import { createReview } from "./ReviewService";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import StarRatingInput from "../../utilities/components/StarRatingInput";
import { useUser } from "../user/UserProvider";
import { useLoadingSpinner } from "../../utilities/components/LoadingSpinnerProvider";
import { X } from "lucide-react";

const schema = object().shape({
  id: number().nullable().optional(),
  title: string().required("Headline is required"),
  rating: number()
    .min(1, "Please rate at least 1 star.")
    .max(5, "Five (5) stars is the maximum rating value.")
    .required("Rating is required"),
  comment: string()
    .test(
      "is-right-size",
      "Comment must be at most 2000 characters",
      (value) => value.trim().length < 2000,
    )
    .required("Review text is required"),
  postedDate: string().required(),
  lastEditedDate: string().notRequired(),
  user: object().required(),
});

const ReviewForm = (props) => {
  const {
    review,
    updateReview,
    toggleModal,
    setState,
    isUpdating = false,
  } = props;
  const [apiError, setApiError] = useState(false);

  const { userProfile } = useUser();
  const setIsLoading = useLoadingSpinner();
  const userSummary = {
    userId: userProfile.userId,
    firstName: userProfile.firstName,
    lastName: userProfile.lastName,
    profilePictureUrl: userProfile.userMedia?.mediaUrl ?? "",
  };

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: review?.id ?? null,
      title: review?.title ?? "",
      rating: review?.rating ?? 0,
      comment: review?.comment ?? "",
      postedDate: review?.postedDate ?? new Date().toISOString(),
      lastEditedDate: review?.lastEditedDate ?? null,
      user: review?.user ?? userSummary,
    },
  });

  const commentValue = watch("comment", "");

  const onSubmit = async (data) => {
    setIsLoading(true);
    if (isUpdating) {
      data.lastEditedDate = new Date().toISOString();
      await updateReview(data, setApiError, setState, setIsLoading);
    } else {
      await createReview(data, setApiError, setState, setIsLoading);
    }
    // Note: We don't auto-close on error to let user fix it, but reset on success is handled by parent or here if needed.
    // Logic kept consistent with original:
    if (!apiError) {
      reset();
      if (toggleModal) toggleModal(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
          <div>
            <h2 className="text-2xl font-serif font-bold text-slate-900 dark:text-white">
              {isUpdating ? "Edit Review" : "Share Your Experience"}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Your feedback helps us improve and helps others choose.
            </p>
          </div>
          <button
            onClick={() => toggleModal(false)}
            className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-slate-500"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-8">
          {/* Rating Section */}
          <div className="flex flex-col items-center justify-center space-y-4 py-4 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
            <span className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              How would you rate your experience?
            </span>
            <Controller
              name="rating"
              control={control}
              render={({ field: { onChange, value } }) => (
                <StarRatingInput value={value} onChange={onChange} size={40} />
              )}
            />
            <p className="text-red-500 text-sm font-medium">
              {errors.rating?.message}
            </p>
          </div>

          <div className="space-y-6">
            {/* Title Input */}
            <div className="space-y-2">
              <label
                htmlFor="title"
                className="block text-sm font-bold text-slate-700 dark:text-slate-200"
              >
                Headline
              </label>
              <input
                placeholder="Sum up your experience in a few words..."
                className={`w-full px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-800 border ${
                  errors.title
                    ? "border-red-500 focus:ring-red-200"
                    : "border-slate-200 dark:border-slate-700 focus:ring-blue-100 dark:focus:ring-blue-900"
                } focus:border-blue-500 outline-none transition-all placeholder:text-slate-400 dark:text-white`}
                {...register("title")}
              />
              <p className="text-red-500 text-xs mt-1">
                {errors.title?.message}
              </p>
            </div>

            {/* Comment Input */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label
                  htmlFor="comment"
                  className="block text-sm font-bold text-slate-700 dark:text-slate-200"
                >
                  Your Review
                </label>
                <span
                  className={`text-xs ${commentValue.length > 1900 ? "text-orange-500" : "text-slate-400"}`}
                >
                  {commentValue.length}/2000
                </span>
              </div>
              <textarea
                placeholder="Tell us what you loved, or what we could do better..."
                maxLength={2000}
                rows={5}
                className={`w-full px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-800 border ${
                  errors.comment
                    ? "border-red-500 focus:ring-red-200"
                    : "border-slate-200 dark:border-slate-700 focus:ring-blue-100 dark:focus:ring-blue-900"
                } focus:border-blue-500 outline-none transition-all resize-none placeholder:text-slate-400 dark:text-white`}
                {...register("comment")}
              />
              <p className="text-red-500 text-xs mt-1">
                {errors.comment?.message}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              onClick={() => toggleModal(false)}
              className="px-6 py-2.5 rounded-lg text-slate-600 dark:text-slate-300 font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-600/30 transition-all hover:-translate-y-0.5"
            >
              {isUpdating ? "Update Review" : "Post Review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewForm;
